import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { 
  ArrowLeft, 
  Globe,
  Store,
  CreditCard,
  Check,
  X,
  Shield
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import NavBar from "../components/NavBar";
import { TrustBadge, SecurityNote } from "../components/EmptyState";

// Toggle Switch Component (defined outside to avoid re-creation during render)
const ToggleSwitch = ({ enabled, onToggle, disabled }) => (
  <button
    onClick={onToggle}
    disabled={disabled}
    className={`w-12 h-7 rounded-full transition-colors relative ${
      enabled ? "bg-primary" : "bg-secondary"
    } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
    data-testid={`toggle-${enabled ? "on" : "off"}`}
  >
    <div className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-transform ${
      enabled ? "translate-x-6" : "translate-x-1"
    }`} />
  </button>
);

const CardSettings = ({ user }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Fetch card settings
  const { data: settings, isLoading } = useQuery({
    queryKey: ["cardSettings"],
    queryFn: async () => {
      const response = await axios.get(`${API}/card/settings`);
      return response.data;
    }
  });

  // Update settings mutation
  const updateMutation = useMutation({
    mutationFn: async (newSettings) => {
      const response = await axios.put(`${API}/card/settings`, newSettings);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["cardSettings"]);
      toast.success("Settings updated");
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || "Failed to update settings");
    }
  });

  const toggleSetting = (key) => {
    if (!settings) return;
    updateMutation.mutate({ [key]: !settings[key] });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="card-settings-page">
      <header className="px-5 pt-4 pb-2 flex items-center gap-4 max-w-lg mx-auto">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/card")}
          className="text-foreground"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="font-heading font-semibold text-xl">Card Settings</h1>
      </header>

      <main className="px-5 max-w-lg mx-auto space-y-6">
        {/* Payment Controls */}
        <div className="glass rounded-2xl overflow-hidden">
          <p className="px-5 pt-5 pb-3 text-xs uppercase tracking-widest text-muted-foreground/70">
            Payment Controls
          </p>
          
          {/* Online Payments */}
          <div className="flex items-center justify-between p-5 border-b border-white/5">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <Globe className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-sm font-medium">Online Payments</p>
                <p className="text-xs text-muted-foreground">Use card for online purchases</p>
              </div>
            </div>
            <ToggleSwitch 
              enabled={settings?.online_payments} 
              onToggle={() => toggleSetting("online_payments")}
              disabled={updateMutation.isPending}
            />
          </div>
          
          {/* In-Store Payments */}
          <div className="flex items-center justify-between p-5 border-b border-white/5">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                <Store className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-sm font-medium">In-Store Payments</p>
                <p className="text-xs text-muted-foreground">Use card at physical stores</p>
              </div>
            </div>
            <ToggleSwitch 
              enabled={settings?.instore_payments} 
              onToggle={() => toggleSetting("instore_payments")}
              disabled={updateMutation.isPending}
            />
          </div>
          
          {/* International Payments */}
          <div className="flex items-center justify-between p-5">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-sm font-medium">International / Travel</p>
                <p className="text-xs text-muted-foreground">Use card outside Jamaica</p>
              </div>
            </div>
            <ToggleSwitch 
              enabled={settings?.international_payments} 
              onToggle={() => toggleSetting("international_payments")}
              disabled={updateMutation.isPending}
            />
          </div>
        </div>

        {/* Current Status Summary */}
        <div className="glass rounded-2xl p-5">
          <p className="text-xs uppercase tracking-widest text-muted-foreground/70 mb-4">
            Current Status
          </p>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Online Payments</span>
              <span className={`flex items-center gap-1 text-sm ${settings?.online_payments ? "text-emerald-400" : "text-rose-400"}`}>
                {settings?.online_payments ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                {settings?.online_payments ? "Enabled" : "Blocked"}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">In-Store Payments</span>
              <span className={`flex items-center gap-1 text-sm ${settings?.instore_payments ? "text-emerald-400" : "text-rose-400"}`}>
                {settings?.instore_payments ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                {settings?.instore_payments ? "Enabled" : "Blocked"}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">International / Travel</span>
              <span className={`flex items-center gap-1 text-sm ${settings?.international_payments ? "text-emerald-400" : "text-rose-400"}`}>
                {settings?.international_payments ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                {settings?.international_payments ? "Enabled" : "Blocked"}
              </span>
            </div>
          </div>
        </div>

        {/* Trust note */}
        <SecurityNote text="Changes take effect instantly. You can adjust these settings anytime." />

        {/* Info Note */}
        <div className="glass rounded-2xl p-4">
          <p className="text-sm text-muted-foreground">
            <span className="text-primary font-medium">Security tip:</span> Disable payment types you don&apos;t use regularly. You can always re-enable them when needed.
          </p>
          <p className="text-xs text-muted-foreground/70 mt-2">
            If a blocked payment type is attempted, the transaction will be declined with a friendly message.
          </p>
        </div>
      </main>

      <NavBar active="wallet" />
    </div>
  );
};

export default CardSettings;
